import { useState } from 'react'
import { useAiSuggestions } from '../../hooks/useAiSuggestions'
import { useAuth } from '../../contexts/AuthContext'
import { SuggestionModal } from './SuggestionModal'
import type { ImproveTextRequest } from '../../types/api'

interface ImproveTextButtonProps {
  text: string
  context: ImproveTextRequest['context']
  onAccept: (improvedText: string) => void
  disabled?: boolean
}

export function ImproveTextButton({
  text,
  context,
  onAccept,
  disabled,
}: ImproveTextButtonProps) {
  const { user } = useAuth()
  const { isLoading, suggestion, getSuggestion, reset } = useAiSuggestions()
  const [showModal, setShowModal] = useState(false)

  const handleClick = async () => {
    if (!text.trim()) return

    const result = await getSuggestion({ text, context, language: 'es' })
    if (result) {
      setShowModal(true)
    }
  }

  const handleAccept = () => {
    if (suggestion?.improved) {
      onAccept(suggestion.improved)
    }
    setShowModal(false)
    reset()
  }

  const handleClose = () => {
    setShowModal(false)
    reset()
  }

  if (!user) {
    return null
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isLoading || !text.trim()}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Mejorar con IA"
      >
        {isLoading ? (
          <>
            <svg
              className="w-3.5 h-3.5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Mejorando...</span>
          </>
        ) : (
          <>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <span>Mejorar con IA</span>
          </>
        )}
      </button>

      <SuggestionModal
        isOpen={showModal}
        onClose={handleClose}
        original={text}
        improved={suggestion?.improved || ''}
        suggestions={suggestion?.suggestions || []}
        onAccept={handleAccept}
      />
    </>
  )
}
